// Storage utilities for sessionStorage and cookies

import type {
  AddressData,
  PersonalInfo,
  SelectedService,
  PropertyData,
  WaterServiceData,
} from '@/types';
import { logger } from '@/utils/logger';

// Cookie management
export function getCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
  return match ? decodeURIComponent(match[2]) : null;
}

export function setCookie(name: string, value: string, days: number = 30): void {
  const expires = new Date();
  expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
  document.cookie = `${name}=${encodeURIComponent(value)};expires=${expires.toUTCString()};path=/`;
}

export function deleteCookie(name: string): void {
  document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
}

// Session storage management
export function saveToSession<T>(key: string, value: T): void {
  try {
    sessionStorage.setItem(key, JSON.stringify(value));
  } catch (error) {
    logger.error({ area: 'storage' }, `Failed to save ${key} to session`, error instanceof Error ? error : undefined);
  }
}

export function loadFromSession(): {
  addressData?: AddressData;
  personalInfo?: PersonalInfo;
  selectedPlans?: SelectedService[];
  propertyData?: PropertyData;
  serviceData?: WaterServiceData;
  waterAvailable?: boolean;
  skipPropertyScreen?: boolean;
} {
  const result: ReturnType<typeof loadFromSession> = {};

  try {
    const addressData = sessionStorage.getItem('addressData');
    if (addressData) result.addressData = JSON.parse(addressData);

    const personalInfo = sessionStorage.getItem('personalInfo');
    if (personalInfo) result.personalInfo = JSON.parse(personalInfo);

    const selectedPlans = sessionStorage.getItem('selectedPlans');
    if (selectedPlans) result.selectedPlans = JSON.parse(selectedPlans);

    const propertyData = sessionStorage.getItem('propertyData');
    if (propertyData) result.propertyData = JSON.parse(propertyData);

    const serviceData = sessionStorage.getItem('serviceData');
    if (serviceData) result.serviceData = JSON.parse(serviceData);

    const waterAvailable = sessionStorage.getItem('waterAvailable');
    if (waterAvailable) result.waterAvailable = waterAvailable === 'true';

    const skipPropertyScreen = sessionStorage.getItem('skipPropertyScreen');
    if (skipPropertyScreen) result.skipPropertyScreen = skipPropertyScreen === 'true';
  } catch (error) {
    logger.error({ area: 'storage' }, 'Failed to load from session', error instanceof Error ? error : undefined);
  }

  return result;
}

export function clearUserData(): void {
  // Clear cookies
  deleteCookie('cp_user_id');
  deleteCookie('cp_cart_data');

  // Clear session storage
  const keysToRemove = [
    'addressData',
    'personalInfo',
    'selectedPlans',
    'propertyData',
    'serviceData',
    'waterAvailable',
    'skipPropertyScreen',
    'internetPlans',
    'electricityPlans',
    'checkoutSteps',
    'view7FormData',
    'esiidData',
    'usageProfile',
    'isApartmentAddress',
    'apartmentWaterChoice',
    'apartmentWaterServiceData',
  ];

  keysToRemove.forEach((key) => {
    sessionStorage.removeItem(key);
  });
}

export function getSessionItem<T>(key: string): T | null {
  try {
    const item = sessionStorage.getItem(key);
    return item ? JSON.parse(item) : null;
  } catch {
    return null;
  }
}
